import _ from 'lodash';
import moment from 'moment';
import api from '@/utils/api';
import TableDataTemplate from './TableDataTemplate';

export function normalizeNumber(num) {
    return parseFloat(Math.round(num * 10000) / 100).toFixed(2);
}

export function updateTableData(data, isShowZeroItems = true) {
    // 從資料模板複製一份
    const RETURN = _.cloneDeep(TableDataTemplate);

    // 使用 groupNo 逐一找到對應項目，塞入 children
    _.forEach(data, (value, key) => {
        const tableItem = RETURN.find((n) => n.id === key);
        if (tableItem) {
            // isShowZeroItems 為 false 時，排除金額為 0 的科目
            const newChildren = data[tableItem.id].map((item) => ({
                id: item.accno,
                name: item.accnam,
                amount: Math.round(item.ntTotalAmount),
                percentage: 0,
                isMinus: tableItem.dC_TYP !== item.dC_TYP,
                level: 1,
            }));

            // 在 isShowZeroItems 為 false 時，只加入金額不為 0 的科目
            if (isShowZeroItems) {
                tableItem.children = tableItem.children.concat(newChildren);
            } else {
                const noneZeroChildren = newChildren.filter((item) => item.amount !== 0);
                tableItem.children = tableItem.children.concat(noneZeroChildren);
            }
        }
    });

    // 加入資料後處理方法
    RETURN.forEach((item, index) => {
        if (item.isTotal && item.sumBy) {
            let sum = 0;
            item.sumBy.forEach((id) => {
                sum += _.chain(RETURN)
                    .find(['id', id])
                    .get('children')
                    .sumBy((o) => (o.isMinus ? -o.amount : o.amount))
                    .value();
            });
            RETURN[index].amount = sum;
        } else if (item.isCalculate) {
            const OPERATOR = item.method[0];
            const FIRST = _.find(RETURN, ['id', item.method[1]]).amount;
            const LAST = _.find(RETURN, ['id', item.method[2]]).amount;

            if (OPERATOR === '-') RETURN[index].amount = FIRST - LAST;
            else if (OPERATOR === '+') RETURN[index].amount = FIRST + LAST;
        } else if (item.level === 0) {
            RETURN[index].amount = _.sumBy(item.children, (o) => (o.isMinus ? -o.amount : o.amount));
        }

        RETURN[index].children = _.sortBy(item.children, 'isMinus');
    });

    const NET_INCOME_TOTAL = _.find(RETURN, ['id', 'netIncomeTotal']).amount;
    RETURN.forEach((item) => {
        const ITEM = item;

        ITEM.percentage = normalizeNumber(ITEM.amount / NET_INCOME_TOTAL);

        ITEM.children.forEach((child) => {
            const c = child;
            c.percentage = normalizeNumber(c.amount / NET_INCOME_TOTAL);
        });
    });

    return RETURN;
}

export function updateMonthlyTableData(yearData, isShowZeroItems = true) {
    // 從資料模板複製一份
    const RETURN = _.cloneDeep(TableDataTemplate);

    yearData.forEach((monthData) => {
        const MONTH = monthData.month;

        RETURN.forEach((item) => {
            const ITEM = item;
            const MONTH_DATA_CHILDREN = monthData[ITEM.id];

            if (MONTH_DATA_CHILDREN && MONTH_DATA_CHILDREN.length) {
                MONTH_DATA_CHILDREN.forEach((o) => {
                    const FOUND = _.find(ITEM.children, ['id', o.accno]);
                    if (FOUND) {
                        FOUND[`amount-m${MONTH}`] = Math.round(o.ntTotalAmount);
                        FOUND[`percentage-m${MONTH}`] = 0;
                    } else {
                        ITEM.children.push({
                            id: o.accno,
                            name: o.accnam,
                            [`amount-m${MONTH}`]: Math.round(o.ntTotalAmount),
                            [`percentage-m${MONTH}`]: 0,
                            isMinus: o.dC_TYP !== item.dC_TYP,
                            level: 1,
                        });
                    }
                });
            } else if (ITEM.children.length) {
                ITEM.children.forEach((child) => {
                    const CHILD = child;
                    CHILD[`amount-m${MONTH}`] = 0;
                    CHILD[`percentage-m${MONTH}`] = 0;
                });
            }

            // 加入資料後處理方法
            if (ITEM.isTotal && ITEM.sumBy) {
                let sum = 0;
                ITEM.sumBy.forEach((id) => {
                    sum += _.chain(RETURN)
                        .find(['id', id])
                        .get('children')
                        .sumBy((o) => (o.isMinus ? -o[`amount-m${MONTH}`] || 0 : o[`amount-m${MONTH}`] || 0))
                        .value();
                });
                ITEM[`amount-m${MONTH}`] = sum;
            } else if (ITEM.isCalculate) {
                const OPERATOR = ITEM.method[0];
                const FIRST = _.find(RETURN, ['id', ITEM.method[1]])[`amount-m${MONTH}`];
                const LAST = _.find(RETURN, ['id', ITEM.method[2]])[`amount-m${MONTH}`];

                if (OPERATOR === '-') ITEM[`amount-m${MONTH}`] = FIRST - LAST;
                else if (OPERATOR === '+') ITEM[`amount-m${MONTH}`] = FIRST + LAST;
            } else if (ITEM.level === 0) {
                ITEM[`amount-m${MONTH}`] = _.sumBy(ITEM.children, (o) => (o.isMinus ? -o[`amount-m${MONTH}`] || 0 : o[`amount-m${MONTH}`]) || 0);
            }

            ITEM.children = _.sortBy(ITEM.children, 'isMinus');
        });
    });

    // 計算總和
    RETURN.forEach((item) => {
        const ITEM = item;
        for (let m = 1; m <= 12; m += 1) {
            ITEM.amount += ITEM[`amount-m${m}`] || 0;
        }

        ITEM.children.forEach((child) => {
            const CHILD = child;
            CHILD.amount = 0;
            let zeroMonthCount = 0;
            for (let m = 1; m <= 12; m += 1) {
                // 項目金額加總
                CHILD.amount += CHILD[`amount-m${m}`] || 0;

                // 順便計算為0的月份數，供後續踢除顯示使用
                if (!CHILD[`amount-m${m}`] || CHILD[`amount-m${m}`] === 0) {
                    zeroMonthCount += 1;
                }
            }
            // 如果12個月都為零，則標記 allZero，供後續踢除顯示使用
            if (zeroMonthCount >= 12) {
                CHILD.allZero = true;
            } else {
                CHILD.allZero = false;
            }
        });
    });

    // 踢除全部月份數字為0的科目
    if (!isShowZeroItems) {
        RETURN.forEach((item) => {
            const ITEM = item;
            ITEM.children = ITEM.children.filter((child) => child.allZero !== true);
        });
    }

    // RETURN.children 項目重新以 accno(id) 排序
    RETURN.forEach((item) => {
        const ITEM = item;
        ITEM.children = _.sortBy(ITEM.children, 'id');
    });

    return RETURN;
}

export async function fetchNormalRowDatas(startDate, endDate) {
    const url = '/AccountingSummaryReport/QueryGaap';
    const queryParams = {
        StartDate: startDate,
        EndDate: endDate,
    };

    try {
        const { data } = await api({
            url,
            method: 'GET',
            params: queryParams,
        });

        return data;
    } catch (ex) {
        console.log(ex);
        return [];
    }
}

export async function fetchMonthlyRowDatas(queryYear) {
    const url = '/AccountingSummaryReport/QueryMultiMonthGaap';
    const queryParams = { year: queryYear };

    try {
        const { data } = await api({
            url,
            method: 'GET',
            params: queryParams,
        });

        return data;
    } catch (ex) {
        console.log(ex);
        return [];
    }
}
